import { useCallback, useEffect, useState } from 'react';
import { authApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { useModeConnexion } from '../../api/modeConnexion';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { tr } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Le rôle tel que le patron le nomme. */
const ROLES = {
  OUVRIER: 'Ouvrier',
  TECHNICIEN: 'Technicien',
  COMPTABLE: 'Comptable',
  SECRETAIRE: 'Secrétaire',
};

/**
 * Comptes de l'équipe, dans Réglages → Mon compte : seul endroit où le patron
 * ferme l'accès d'un collaborateur. La fiche de l'employé et son invitation
 * ne le proposent plus : un compte se ferme ici, nulle part ailleurs.
 *
 * <p>Elle dit aussi comment inviter une secrétaire, un technicien ou la
 * comptable : l'invitation part de leur fiche, dans Équipe & paie — le bouton
 * y mène.
 *
 * @param {object} props propriétés du composant
 * @param {(route: string) => void} [props.onNav] navigation vers Équipe & paie
 * @returns {JSX.Element|null} la carte, pour le patron seul
 */
export function ComptesEquipeCard({ onNav }) {
  const { session } = useSession();
  // Quand le code à six chiffres vaut pour tous, l'ancien accès par code est à
  // réinviter.
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;
  const patron = session?.role === 'patron';
  const [comptes, setComptes] = useState(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(null);

  const charge = useCallback(() => {
    Promise.resolve()
      .then(() => authApi.comptes())
      .then((l) => setComptes((l || []).filter((c) => ROLES[c.role] && c.actif !== false)))
      .catch((e) => setErreur(e?.message || 'La liste n’a pas pu être lue.'));
  }, []);

  useEffect(() => {
    if (patron) charge();
  }, [patron, charge]);

  const ferme = async (c) => {
    if (!window.confirm(tr('Fermer cet accès ? La personne sera déconnectée sur-le-champ.')))
      return;
    setErreur('');
    setEnCours(c.id);
    try {
      await authApi.desactiveCompte(c.id);
      charge();
    } catch (e) {
      setErreur(e?.message || 'L’accès n’a pas pu être fermé.');
    } finally {
      setEnCours(null);
    }
  };

  if (!patron) return null;
  return (
    <Card style={{ marginBottom: 12 }}>
      <Bar
        icon="👥"
        title="Comptes de l’équipe"
        color={C.navy}
        sub="Inviter un collaborateur, ou fermer son accès"
      />
      <div style={{ padding: 13 }}>
        <div
          data-testid="inviter-equipe"
          style={{
            background: C.navyXL,
            borderRadius: 9,
            padding: '10px 11px',
            marginBottom: 10,
            fontSize: 10,
            lineHeight: 1.55,
            color: C.ink,
          }}
        >
          <div style={{ fontWeight: 800, color: C.navy, marginBottom: 4 }}>
            Inviter une secrétaire, un technicien ou la comptable
          </div>
          <div>1. Équipe & paie → « ＋ Nouvel employé » : son nom, son e-mail et sa fonction.</div>
          <div>
            2. Ouvrez sa fiche → « ✉️ Inviter » → choisissez son rôle : Secrétaire, Technicien ou
            Comptable.
          </div>
          <div>
            3. Elle reçoit un e-mail, crée son mot de passe, et ne voit que ce que son rôle permet :
            ni votre chiffre d’affaires ni votre bénéfice pour la secrétaire et le technicien.
          </div>
          {onNav ? (
            <button
              type="button"
              onClick={() => onNav('equipe')}
              style={{
                marginTop: 8,
                width: '100%',
                border: 'none',
                borderRadius: 9,
                padding: '10px',
                fontSize: 11,
                fontWeight: 800,
                cursor: 'pointer',
                background: C.navy,
                color: '#fff',
              }}
            >
              ➕ Inviter un collaborateur
            </button>
          ) : null}
        </div>
        {erreur && (
          <div role="alert" style={{ fontSize: 10, color: C.red, marginBottom: 6 }}>
            {erreur}
          </div>
        )}
        {comptes === null && !erreur && (
          <div role="status" style={{ fontSize: 10, color: C.muted }}>
            Chargement…
          </div>
        )}
        {comptes && comptes.length === 0 && (
          <div style={{ fontSize: 10, color: C.muted }}>Aucun compte de collaborateur ouvert.</div>
        )}
        {(comptes || []).map((c) => (
          <div
            key={c.id}
            data-testid="compte-equipe"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              padding: '8px 0',
              borderTop: '1px solid ' + C.border,
              fontSize: 10.5,
            }}
          >
            <span style={{ minWidth: 0 }}>
              <b>
                {c.nomComplet ||
                  (c.accesParPin
                    ? codeOuvrier
                      ? tr('Accès par code PIN')
                      : tr('Ancien accès par code (à réinviter)')
                    : c.email)}
              </b>
              <span style={{ color: C.muted }}> · {tr(ROLES[c.role])}</span>
            </span>
            <button
              disabled={enCours === c.id}
              onClick={() => ferme(c)}
              style={{
                border: '1px solid ' + C.red,
                background: C.white,
                color: C.red,
                borderRadius: 8,
                padding: '6px 9px',
                fontSize: 9.5,
                fontWeight: 800,
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Fermer l’accès
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}
